import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicPageShell from '../components/PublicPageShell';
import SupportChatWidget from '../components/SupportChatWidget';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ChatsCircle, FileText, Star, ArrowRight, CheckCircle, Lightbulb,
  Question, Download, ChartLineUp, Lightning, Sparkle,
} from '@phosphor-icons/react';
import { supportApi } from '../lib/api';

const CATEGORIES = [
  { key: 'billing',   label: 'Billing & subscriptions' },
  { key: 'technical', label: 'Technical issue' },
  { key: 'sales',     label: 'Sales & pricing' },
  { key: 'feedback',  label: 'Product feedback' },
  { key: 'other',     label: 'Other' },
];

const SHEETS = [
  { title: 'BIL Buyer\'s Guide 2026', desc: 'A 24-page deep-dive — features, security posture, ROI math, and an onboarding plan.', pages: '24 pages · PDF', icon: FileText, tint: '#002677' },
  { title: 'Procurement RFQ Template', desc: 'Plug-and-play multi-round RFQ template with supplier scoring matrix.', pages: '6 pages · XLSX', icon: ChartLineUp, tint: '#00B140' },
  { title: 'M&A Data-Room Index', desc: 'Standardised folder taxonomy used by 200+ deal teams. Save weeks of setup.', pages: '4 pages · PDF', icon: FileText, tint: '#8347AD' },
  { title: 'GDPR + SOC 2 Crosswalk', desc: 'The single overlap-and-gap matrix you need before your next audit.', pages: '12 pages · PDF', icon: Lightning, tint: '#E4002B' },
];

export default function SupportPage() {
  return (
    <PublicPageShell
      testId="support-page"
      eyebrow="Support"
      title="We're here to help."
      subtitle="Chat with BIL Assistant — our AI copilot for instant answers on plans, billing, security and integrations. For account-specific issues, send a detailed enquiry and we'll respond within 1 business day."
      ctaPrimary={{ label: 'See pricing', to: '/pricing' }}
      ctaSecondary={{ label: 'Browse docs', to: '/resources/docs' }}
    >
      {/* Chat-first hero */}
      <section className="py-16 lg:py-20 bg-background">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-2 lg:sticky lg:top-6 space-y-6" data-testid="support-side">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">BIL Assistant</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
                Get instant answers, 24/7.
              </h2>
              <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                Ask about plans, billing, security, integrations, or product features.
                For account-specific issues, BIL Assistant will offer to start a callback request — our team will respond within 1 business day.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3" data-testid="support-quicklinks">
              <Link to="/resources/docs" className="bg-card border border-border rounded-sm p-4 hover:border-foreground/30 hover:shadow-md transition-all flex flex-col" data-testid="docs-card">
                <Question size={20} weight="duotone" className="text-[#002677] mb-2" />
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground">Self-serve</p>
                <p className="font-semibold text-sm mt-1">Documentation</p>
              </Link>
              <a href="#enquiry-form" className="bg-card border border-border rounded-sm p-4 hover:border-foreground/30 hover:shadow-md transition-all flex flex-col" data-testid="enquiry-card">
                <ChatsCircle size={20} weight="duotone" className="text-[#002677] mb-2" />
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground">Detailed enquiry</p>
                <p className="font-semibold text-sm mt-1">Use the form</p>
              </a>
            </div>
            <div className="bg-[#f5f6fa] dark:bg-muted/20 border border-border rounded-sm p-4 flex items-start gap-3">
              <Sparkle size={18} weight="fill" className="text-[#FFD100] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Tip</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  The assistant has full context on BIL plans, security posture and product capabilities.
                  Try: <em>&ldquo;Compare Professional and Business&rdquo;</em>.
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3" id="chat-column" data-testid="chat-column">
            <SupportChatWidget />
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="enquiry-form" className="py-16 lg:py-20 bg-[#f5f6fa] dark:bg-muted/20 border-y border-border">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">Submit an enquiry</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Need a human follow-up?</h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-xl mx-auto">
              Drop us a detailed message and a specialist will reply within 1 business day.
            </p>
          </div>
          <EnquiryForm />
        </div>
      </section>

      {/* Sheets */}
      <section className="py-16 lg:py-20 bg-background">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#002677] mb-3">Sheets &amp; templates</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Free, practitioner-grade resources.</h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-xl mx-auto">
              Ask BIL Assistant for any of these — it&rsquo;ll help you get the latest version.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="sheets-grid">
            {SHEETS.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.title}
                  to="/support#chat-column"
                  className="bg-card border border-border rounded-sm p-5 hover:border-foreground/30 hover:shadow-md transition-all flex flex-col"
                  data-testid={`sheet-${s.title.toLowerCase().replace(/[^a-z0-9]+/g,'-').slice(0,40)}`}
                >
                  <div className="h-10 w-10 rounded-sm flex items-center justify-center mb-3" style={{ background: `${s.tint}1A` }}>
                    <Icon size={18} weight="duotone" color={s.tint} />
                  </div>
                  <p className="font-heading text-base font-semibold tracking-tight leading-snug">{s.title}</p>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed flex-1">{s.desc}</p>
                  <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-[11px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
                    <span>{s.pages}</span>
                    <span className="inline-flex items-center gap-1.5" style={{ color: s.tint }}>
                      <Download size={12} weight="bold" /> Request
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Survey */}
      <section className="py-16 bg-[#f5f6fa] dark:bg-muted/20 border-t border-border">
        <div className="max-w-[800px] mx-auto px-6">
          <SurveyCard />
        </div>
      </section>
    </PublicPageShell>
  );
}

// =======================================================================
function EnquiryForm() {
  const [form, setForm] = useState({ full_name: '', email: '', company: '', category: 'other', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await supportApi.createEnquiry(form);
      setSubmitted(true);
      toast.success("Thanks — we'll get back to you within 1 business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-card border border-border rounded-sm p-8 text-center" data-testid="enquiry-success">
        <div className="h-14 w-14 rounded-full bg-[#00B140]/15 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={28} weight="fill" className="text-[#00B140]" />
        </div>
        <h3 className="font-heading text-2xl font-semibold tracking-tight">
          Got it, {form.full_name.split(' ')[0]}.
        </h3>
        <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
          A specialist will reply to <strong>{form.email}</strong> within 1 business day.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-card border border-border rounded-sm p-6 sm:p-8 space-y-5" data-testid="enquiry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Full name *</Label>
          <Input required value={form.full_name} onChange={(e) => set('full_name', e.target.value)} className="mt-1.5 rounded-sm" data-testid="enq-name" placeholder="Jane Doe" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Work email *</Label>
          <Input required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="mt-1.5 rounded-sm" data-testid="enq-email" placeholder="jane@company.com" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Company</Label>
          <Input value={form.company} onChange={(e) => set('company', e.target.value)} className="mt-1.5 rounded-sm" data-testid="enq-company" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Category</Label>
          <Select value={form.category} onValueChange={(v) => set('category', v)}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="enq-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Subject *</Label>
        <Input required value={form.subject} onChange={(e) => set('subject', e.target.value)} className="mt-1.5 rounded-sm" data-testid="enq-subject" placeholder="Short summary of your enquiry" />
      </div>
      <div>
        <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">How can we help? *</Label>
        <Textarea required value={form.message} onChange={(e) => set('message', e.target.value)} className="mt-1.5 rounded-sm min-h-[140px]" data-testid="enq-message" placeholder="Share as much detail as you can." />
      </div>
      <Button type="submit" disabled={submitting} className="w-full rounded-sm h-12 font-medium" style={{ background: '#002677' }} data-testid="enq-submit">
        {submitting ? 'Submitting…' : 'Send enquiry'}
        <ArrowRight size={16} className="ml-2" weight="bold" />
      </Button>
    </form>
  );
}

// =======================================================================
function SurveyCard() {
  const [tab, setTab] = useState('csat');
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const submit = async () => {
    if (tab === 'csat' && score === 0) { toast.error('Please pick a rating.'); return; }
    if (tab === 'feature' && !comment.trim()) { toast.error('Tell us what you\'d like to see.'); return; }
    setSubmitting(true);
    try {
      await supportApi.submitSurvey({
        type: tab,
        score: tab === 'csat' ? score : undefined,
        comment: comment || undefined,
        email: email || undefined,
      });
      setSubmitted(tab);
      toast.success('Thanks for the feedback!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6 sm:p-8" data-testid="survey-card">
      <div className="flex items-center gap-3 mb-1">
        <Lightbulb size={22} weight="duotone" className="text-[#FFD100]" />
        <h2 className="font-heading text-2xl font-semibold tracking-tight">Quick survey</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">30 seconds, anonymous unless you add your email.</p>
      <div className="flex border-b border-border mb-5" data-testid="survey-tabs">
        <button onClick={() => { setTab('csat'); setSubmitted(null); }} className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${tab === 'csat' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="survey-tab-csat">
          Rate BIL
          {tab === 'csat' && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#002677]" />}
        </button>
        <button onClick={() => { setTab('feature'); setSubmitted(null); }} className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${tab === 'feature' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="survey-tab-feature">
          Feature request
          {tab === 'feature' && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#002677]" />}
        </button>
      </div>
      {submitted === tab ? (
        <div className="text-center py-6" data-testid="survey-success">
          <CheckCircle size={28} weight="fill" className="text-[#00B140] mx-auto mb-2" />
          <p className="font-medium">Thanks for the feedback!</p>
          <p className="text-xs text-muted-foreground mt-1">Our product team reviews every submission.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {tab === 'csat' && (
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">How satisfied are you?</Label>
              <div className="mt-3 flex items-center gap-2" data-testid="csat-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setScore(n)} className="p-2 rounded-sm hover:bg-accent/40 transition-colors" data-testid={`csat-star-${n}`} aria-label={`${n} stars`}>
                    <Star size={28} weight={n <= score ? 'fill' : 'regular'} className={n <= score ? 'text-[#FFD100]' : 'text-muted-foreground/40'} />
                  </button>
                ))}
                {score > 0 && (
                  <span className="ml-2 text-sm text-muted-foreground">{['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][score]}</span>
                )}
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">
              {tab === 'csat' ? 'Anything to add? (optional)' : 'What would you like to see? *'}
            </Label>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} className="mt-1.5 rounded-sm min-h-[100px]" data-testid="survey-comment" placeholder={tab === 'csat' ? 'Anything else you\'d like to share' : 'Describe the feature, problem to solve, or workflow you\'d like.'} />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Email (optional — only if you want a follow-up)</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-sm" data-testid="survey-email" placeholder="you@company.com" />
          </div>
          <Button onClick={submit} disabled={submitting} className="w-full rounded-sm h-11 font-medium" style={{ background: '#002677' }} data-testid="survey-submit">
            {submitting ? 'Submitting…' : 'Submit feedback'}
            <ArrowRight size={16} className="ml-2" weight="bold" />
          </Button>
        </div>
      )}
    </div>
  );
}
